import { gridViewManager } from '@/utils/grid'
import { useDriverAnalytics } from '@api/useDriverAnalytics'
import { ResourcePlannerActionButtons } from '@components/DriverManagement/ResourcePlanner/ActionButtons'
import { getColumnDefs } from '@components/DriverManagement/ResourcePlanner/getColumnDefs'
import { GraviGrid } from '@gravitate-js/excalibrr'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const STORAGE_KEY = 'transportation-resource-planner'

export function ResourcePlanner() {
  const gridAPIRef = useRef(null)
  const { value: dayRange, setValue: setDayRange } = useLocalStorage('transportation-resource-planner-day-range', {
    from: -7,
    to: 23,
  })

  const [plannerGridConfig, setPlannerGridConfig] = useState(null)

  const [filters, setFilters] = useState({
    from: moment()
      .add(dayRange.from || dayRange.from === 0 ? dayRange.from : -7, 'day')
      .format('YYYY-MM-DDT00:00:00'),
    to: moment()
      .add(dayRange.to || dayRange.to === 0 ? dayRange.to : 23, 'day')
      .format('YYYY-MM-DDT00:00:00'),
  })

  const { updateSetting, getSetting } = useUserSettings()
  const { useDriverResourceQuery } = useDriverAnalytics()
  const { data, isPending, isFetching } = useDriverResourceQuery({ start_date: filters.from, end_date: filters.to })

  const setDayRangeWrapper = (value) => {
    const currentConfig = window.localStorage.getItem('gridConfig::transportation-resource-planner')
    const currentConfigParsed = JSON.parse(currentConfig)?.column
    setPlannerGridConfig(currentConfigParsed)
    setDayRange(value)
    updateSetting('transportation-resource-planner-day-range', value)
  }

  const dayRangeArray = useMemo(() => {
    const { from, to } = filters
    const startDate = moment(from)
    const endDate = moment(to)

    return Array.from({ length: endDate.diff(startDate, 'days') + 1 }, (_, index) =>
      startDate.clone().add(index, 'day').format('YYYY-MM-DD')
    )
  }, [filters])

  useEffect(() => {
    getSetting('transportation-resource-planner-day-range').then((value) => {
      if (value) setDayRange(value)
      else setDayRange({ from: -7, to: 23 })
    })
  }, [])

  const columnDefs = useMemo(() => (dayRangeArray && data ? getColumnDefs(dayRangeArray) : []), [dayRangeArray, data])
  const getRowId = useCallback((row) => row?.data?.key, [])
  const agPropOverrides = useMemo(
    () => ({
      groupDefaultExpanded: -1,
      getRowId,
      groupDisplayType: 'multipleColumns',
      autoGroupColumnDef: {
        headerClass: 'suppress-column-move',
        minWidth: 200,
        cellRendererParams: {
          suppressCount: true,
        },
        pinned: 'left',
      },
      suppressAggFuncInHeader: true,
    }),
    []
  )
  useEffect(() => {
    if (columnDefs?.length > 0 && gridAPIRef.current) {
      const columns = plannerGridConfig // || gridAPIRef.current.getColumnDefs()
      const newColumns = columnDefs.map((col) => {
        const existingCol = columns?.find((c) => c.colId === col.field)
        if (existingCol) {
          return { ...col, ...existingCol }
        }
        return col
      })
      gridAPIRef.current.setColumnDefs(newColumns)
      gridAPIRef.current.redrawRows()
    }
  }, [columnDefs, gridAPIRef.current])

  return (
    <div style={{ height: 'calc(100vh - 200px)', width: '100%' }}>
      <GraviGrid
        loading={isPending || isFetching}
        columnDefs={columnDefs}
        externalRef={gridAPIRef}
        rowData={data}
        style={{ height: '100%', width: '100%' }}
        className='resource-planner-grid'
        storageKey={STORAGE_KEY}
        controlBarProps={{
          title: 'Transportation Resource Planner',
          actionButtons: (
            <ResourcePlannerActionButtons
              currentFilters={filters}
              setCurrentFilters={setFilters}
              dayRangeStorage={dayRange}
              setDayRangeStorage={setDayRangeWrapper}
              filterDayFormat='YYYY-MM-DDT00:00:00'
              gridAPIRef={gridAPIRef}
            />
          ),
        }}
        agPropOverrides={{
          ...agPropOverrides,
          domLayout: 'normal',
        }}
        onGridConfigChanged={(gridConfig) => {
          updateSetting(`gridConfig::${STORAGE_KEY}`, gridConfig)
        }}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
    </div>
  )
}
